import { useMemo, useState } from "react";
import { Check, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { categoriesFor } from "@/lib/catalog";
import { fmtNum } from "@/lib/format";
import { BigButton, Chip, inputCls } from "@/components/ui-kit";

const QUICK = [-10, -5, 5, 10, 20];

export default function BulkPrice({ products }) {
  const { t, view, dispatch } = useApp();
  const [scope, setScope] = useState("all");
  const [mode, setMode] = useState("percent");
  const [value, setValue] = useState("");

  const rows = useMemo(() => {
    const v = Number(value);
    if (!v) return [];
    return products.filter((p) => scope === "all" || p.category === scope).map((p) => {
      const next = Math.max(0, Math.round(mode === "percent" ? p.price * (1 + v / 100) : p.price + v));
      return { id: p.id, name: p.name, old: p.price, next };
    }).filter((r) => r.next !== r.old);
  }, [products, scope, mode, value]);

  const apply = () => {
    if (dispatch("price.bulk", { changes: rows.map((r) => ({ id: r.id, price: r.next, base_price: r.old })) })) {
      toast.success(t("prices_updated", { n: rows.length }));
      setValue("");
    }
  };

  return (
    <div className="space-y-4" data-testid="bulk-price-panel">
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        <Chip active={scope === "all"} onClick={() => setScope("all")} data-testid="bulk-scope-all">{t("all")}</Chip>
        {categoriesFor(view.shop?.type).map(({ key, icon: Icon }) => <Chip key={key} active={scope === key} onClick={() => setScope(key)} data-testid={`bulk-scope-${key}`}><Icon className="w-4 h-4" />{t(`cat_${key}`)}</Chip>)}
      </div>
      <div className="flex gap-2">
        <Chip active={mode === "percent"} onClick={() => setMode("percent")} data-testid="bulk-mode-percent">%</Chip>
        <Chip active={mode === "amount"} onClick={() => setMode("amount")} data-testid="bulk-mode-amount">± {t("amount")}</Chip>
        <input dir="ltr" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder="+10 / -5" className={`${inputCls} font-bold text-center`} data-testid="bulk-value-input" />
      </div>
      {mode === "percent" && (
        <div className="flex gap-2 flex-wrap">
          {QUICK.map((q) => <Chip key={q} active={Number(value) === q} onClick={() => setValue(String(q))} data-testid={`bulk-quick-${q}`}>{q > 0 ? `+${q}` : q}%</Chip>)}
        </div>
      )}
      {rows.length > 0 && (
        <div className="rounded-2xl bg-white border border-border divide-y divide-border" data-testid="bulk-preview">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center justify-between p-3 gap-2">
              <span className="font-bold truncate">{r.name}</span>
              <span className="flex items-center gap-2 shrink-0" dir="ltr">
                <span className="text-muted-foreground line-through">{fmtNum(r.old)}</span>
                <ArrowLeftRight className="w-4 h-4" />
                <span className={`font-extrabold ${r.next > r.old ? "text-destructive" : "text-[#1F9D55]"}`}>{fmtNum(r.next)}</span>
              </span>
            </div>
          ))}
        </div>
      )}
      <BigButton disabled={!rows.length} onClick={apply} className="w-full" data-testid="bulk-apply-btn"><Check className="w-5 h-5" />{t("apply_to", { n: rows.length })}</BigButton>
    </div>
  );
}
